import { useState } from "react";
import { apiClient, type Api } from "../../../services/api";
import { displayToast } from "../../../components/app/AppToast";
import { useTranslation } from "react-i18next";
import type { ArticleDetail } from "../../../features/article/hooks/useArticleData";

type ContentDetail = Api["get"]["/webapp/public/content/:id"]["output"]["body"];

export type PlaylistPanelContentItem =
  | ArticleDetail
  | Extract<ContentDetail, { content_format: "Video" }>
  | Extract<ContentDetail, { content_format: "Infographic" }>
  | Extract<ContentDetail, { content_format: "Audio" }>;

type PlaylistPanelContentState =
  | { status: "closed" }
  | { status: "loading" }
  | { status: "ready"; content: PlaylistPanelContentItem };

export function usePlaylistPanelContent() {
  const { t } = useTranslation();
  const [state, setState] = useState<PlaylistPanelContentState>({
    status: "closed",
  });

  async function open(contentId: string) {
    setState({ status: "loading" });
    try {
      const { body } = await apiClient.get("/webapp/public/content/:id", {
        param: { id: contentId },
      });
      switch (body.content_format) {
        case "Video":
        case "Infographic":
        case "Audio": {
          setState({ status: "ready", content: body });
          break;
        }
        case "Nectar": {
          setState({ status: "closed" });
          break;
        }
      }
    } catch {
      setState({ status: "closed" });
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function openArticle(slug: string) {
    setState({ status: "loading" });
    try {
      const { body } = await apiClient.get("/webapp/public/articles/:slug", {
        param: { slug },
        query: {},
      });
      setState({ status: "ready", content: body });
    } catch {
      setState({ status: "closed" });
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  function close() {
    setState({ status: "closed" });
  }

  return { state, open, openArticle, close };
}
